import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Portal
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPrice } from '../services/bookingService';
import { parseISO, format } from 'date-fns';

const Confirmation = ({ 
  bookingData, 
  clinic,
  onConfirm,
  loading = false
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth <= 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const formatAppointmentTime = () => {
    console.log('Appointment time object keys:', Object.keys(bookingData?.appointmentTime || {}));
    
    // TIMEZONE FIX: First check if we're on a payment confirmation page
    // and have a time stored in sessionStorage (most reliable)
    const isPaymentCompletionPage = window.location.href.includes('/payment-complete') || 
                                    window.location.href.includes('success=true') ||
                                    window.location.href.includes('session_id=');
                                    
    if (isPaymentCompletionPage) {
      const storedTime = sessionStorage.getItem('appointment_time_display');
      if (storedTime) {
        console.log('Using stored appointment time from sessionStorage:', storedTime);
        return storedTime;
      }
    }
    
    // FIXED TIME HANDLING: When we have an ISO timestamp, create a consistent time format
    if (bookingData?.appointmentTime?.iso) {
      try {
        const startTimeISO = bookingData.appointmentTime.iso;
        console.log('Formatting ISO time directly:', startTimeISO);
        
        // Parse ISO time and format it in Melbourne timezone to match what was selected
        const startTime = parseISO(startTimeISO);
        
        // TIMEZONE FIX: Always format times in Melbourne timezone
        // This ensures the confirmation page shows the same time that was selected
        const formattedTime = new Intl.DateTimeFormat('en-AU', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'Australia/Melbourne'
        }).format(startTime);
        console.log('Using exact time (no adjustment):', formattedTime);
        
        // Store in sessionStorage for payment completion page to use
        sessionStorage.setItem('appointment_time_display', formattedTime);
        return formattedTime;
      } catch (err) {
        console.error('Error in corrected time formatting:', err);
      }
    }
    
    // Fallbacks in case the direct ISO time formatting failed
    
    // Check for time_display from button selection
    if (bookingData?.appointmentTime?.time_display) {
      console.log('Using time_display from selection:', bookingData.appointmentTime.time_display);
      // Store in sessionStorage for payment completion page to use
      sessionStorage.setItem('appointment_time_display', bookingData.appointmentTime.time_display);
      return bookingData.appointmentTime.time_display;
    }
    
    // Check for Melbourne-formatted time
    if (bookingData?.appointmentTime?.formatted_melbourne) {
      console.log('Using formatted_melbourne time:', bookingData.appointmentTime.formatted_melbourne);
      // Store in sessionStorage for payment completion page to use
      sessionStorage.setItem('appointment_time_display', bookingData.appointmentTime.formatted_melbourne);
      return bookingData.appointmentTime.formatted_melbourne;
    }
    
    // Check for regular formatted time
    if (bookingData?.appointmentTime?.formatted) {
      console.log('Using formatted time from API:', bookingData.appointmentTime.formatted);
      // Store in sessionStorage for payment completion page to use
      sessionStorage.setItem('appointment_time_display', bookingData.appointmentTime.formatted);
      return bookingData.appointmentTime.formatted;
    }
    
    // If none of the above work, return unknown
    console.log('No valid time format found');
    return 'Unknown time';
  };
  
  // Check for post-payment scenario - if we're on payment-complete but have incomplete data
  const isPaymentCompletePath = window.location.href.includes('payment-complete');
  const hasSessionId = new URLSearchParams(window.location.search).get('session_id');

  // Ensure we have all necessary data
  if (!bookingData || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
    // If we're on payment-complete page, we should try to restore from sessionStorage
    if (isPaymentCompletePath || hasSessionId) {
      try {
        console.log('Missing booking data on payment completion page, checking sessionStorage...');
        const savedSession = sessionStorage.getItem('bookingSession');
        
        if (savedSession) {
          console.log('Found saved booking session, using for confirmation');
          // Instead of trying to modify the bookingData prop, we'll render alternative content
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Booking Successful
              </Typography>
              
              <Alert severity="success" sx={{ mb: 3 }}>
                Your appointment has been booked successfully. You should receive a confirmation email shortly.
              </Alert>
              
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" gutterBottom sx={{backgroundColor: 'primary.main', padding: '10px', color: 'white'}}>
                  🎉 Appointment Confirmed! 🎉
                </Typography>
                <Typography>
                  Your appointment has been booked successfully. You should receive a confirmation email shortly.
                </Typography>
                
                <Box sx={{ mt: 4 }}>
                  <Button 
                    variant="contained"
                    sx={{fontSize: '18px', padding: '15px'}}
                    color="primary"
                    onClick={() => {
                      // Attempt to return to clinic home
                      const pathParts = window.location.pathname.split('/');
                      const clinicStub = pathParts[1]; // The first segment after the domain
                      window.location.href = `/${clinicStub || ''}`;
                    }}
                  >
                    Return to Home
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        }
      } catch (e) {
        console.error('Error recovering booking data from sessionStorage:', e);
      }
    }
    
    return (
      <Alert severity="error">
        Booking information is incomplete. Please go back and complete all required steps.
      </Alert>
    );
  }
  
  // Check if this is a mobile browser
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  console.log('Browser detection - Mobile:', isMobile);
  
  // On payment-complete path, always consider payment as completed
  if (window.location.pathname.includes('payment-complete')) {
    console.log('URL includes payment-complete path, payment is considered complete');
    
    // Force payment status to complete if not already
    if (!bookingData.payment || bookingData.payment.status !== 'complete') {
      console.log('Setting payment status to complete based on URL path');
      
      // Get any transaction ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      const transactionId = urlParams.get('id') || urlParams.get('transaction_id') || sessionId || 'stripe-payment';
      
      // Modify bookingData directly (not ideal but necessary for this case)
      bookingData.payment = {
        status: 'complete',
        transactionId: transactionId
      };
    }
  }
  // For other paths, check payment status
  else if (!bookingData.payment || bookingData.payment.status !== 'complete') {
    // Check URL parameters for payment completion evidence
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionId = urlParams.get('session_id');
    const hasSuccess = urlParams.get('success') === 'true';
    
    // If we have a session ID and success flag in the URL, assume payment is complete
    if (hasSessionId && hasSuccess) {
      console.log('Found Stripe session ID and success flag in URL, assuming payment is complete');
      // Set payment status to complete
      bookingData.payment = {
        status: 'complete',
        transactionId: hasSessionId
      };
    } else {
      console.log('Missing payment status in booking data, checking URL params for evidence');
      
      // Check if any other URL parameters indicate payment completion
      const hasTransactionId = urlParams.get('id') || urlParams.get('transaction_id');
      const hasAccessCode = urlParams.get('AccessCode');
      
      if (hasTransactionId || hasAccessCode) {
        console.log('Found payment evidence in URL parameters:', { hasTransactionId, hasAccessCode });
        // Set payment status to complete
        bookingData.payment = {
          status: 'complete',
          transactionId: hasTransactionId || 'payment-complete'
        };
      } else {
        console.log('No payment evidence found in booking data or URL');
        return (
          <Alert severity="warning">
            Payment process must be completed before confirming booking.
          </Alert>
        );
      }
    }
  }
  
  return (
    <Box sx={{ pb: { xs: 10, sm: 0 } }}>
      <Typography variant="h6" gutterBottom>
        Confirm Your Booking Details
      </Typography>
      
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="success.main">
                  Booking Ready
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Clinic
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {clinic?.name || 'Unknown Clinic'}
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Service
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.service.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.service.duration_in_minutes} minutes
              </Typography>
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Practitioner
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.practitioner.name}
              </Typography>
            </Grid>
            
            {/* Removed date & time section to avoid confusion */}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Patient
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {bookingData.patient.first_name} {bookingData.patient.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.patient.email}
              </Typography>
              {bookingData.patient.phone && (
                <Typography variant="body2" color="text.secondary">
                  {bookingData.patient.phone}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {bookingData.notes && (
              <>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Notes
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {bookingData.notes}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
              </>
            )}
            
          </Grid>
        </CardContent>
      </Card>
      
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 3, 
          textAlign: 'center',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderTop: '2px solid #e0e0e0',
          marginTop: 2
        }}
      >
        <Typography variant="body1" paragraph>
          Please confirm your booking to finalize your appointment. You will be booked at your selected time and an email confirmation will be sent to you.
        </Typography>
        
        {/* Regular button for desktop */}
        {!isMobileView && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              minHeight: 56
            }}
            onClick={() => {
              onConfirm();
            }}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Processing...' : 'Confirm Booking'}
          </Button>
        )}
      </Paper>
      
      {/* Fixed button for mobile using Portal */}
      {isMobileView && (
        <Portal>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              position: 'fixed',
              bottom: 20,
              left: 20,
              right: 20,
              width: 'calc(100% - 40px)',
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              minHeight: 56,
              zIndex: 9999,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
            onClick={() => {
              // Call the confirmation function
              onConfirm();
              
              // For mobile browsers, show a success message immediately
              if (isMobile) {
                // Set a short timeout to allow the server request to be sent first
                setTimeout(() => {
                  // Show success message for mobile browsers
                  const successBox = document.createElement('div');
                  successBox.style.position = 'fixed';
                  successBox.style.top = '0';
                  successBox.style.left = '0';
                  successBox.style.width = '100%';
                  successBox.style.height = '100%';
                  successBox.style.backgroundColor = 'white';
                  successBox.style.zIndex = '9999';
                  successBox.style.padding = '20px';
                  successBox.style.display = 'flex';
                  successBox.style.flexDirection = 'column';
                  successBox.style.alignItems = 'center';
                  successBox.style.justifyContent = 'center';
                  
                  // Create success message
                  const messageDiv = document.createElement('div');
                  messageDiv.style.backgroundColor = '#2e7d32'; // Success green
                  messageDiv.style.color = 'white';
                  messageDiv.style.padding = '10px';
                  messageDiv.style.borderRadius = '4px';
                  messageDiv.style.marginBottom = '20px';
                  messageDiv.style.textAlign = 'center';
                  messageDiv.style.width = '100%';
                  messageDiv.innerHTML = '🎉 Appointment Confirmed! 🎉';
                  
                  // Create text message
                  const textDiv = document.createElement('div');
                  textDiv.style.marginBottom = '30px';
                  textDiv.style.textAlign = 'center';
                  textDiv.innerHTML = 'Your appointment has been booked successfully. You should receive a confirmation email shortly with all details.';
                  
                  // Create home button
                  const homeButton = document.createElement('button');
                  homeButton.style.backgroundColor = '#1976d2'; // Primary blue
                  homeButton.style.color = 'white';
                  homeButton.style.border = 'none';
                  homeButton.style.borderRadius = '4px';
                  homeButton.style.padding = '15px';
                  homeButton.style.fontSize = '18px';
                  homeButton.style.cursor = 'pointer';
                  homeButton.innerHTML = 'Return to Home';
                  homeButton.onclick = () => {
                    // Return to clinic home
                    const pathParts = window.location.pathname.split('/');
                    const clinicStub = pathParts[1]; // The first segment after the domain
                    window.location.href = `/${clinicStub || ''}`;
                  };
                  
                  // Add all elements to the success box
                  successBox.appendChild(messageDiv);
                  successBox.appendChild(textDiv);
                  successBox.appendChild(homeButton);
                  
                  // Add to document body
                  document.body.appendChild(successBox);
                }, 300);
              }
            }}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Processing...' : 'Confirm Booking'}
          </Button>
        </Portal>
      )}
    </Box>
  );
};

export default Confirmation;